import { Chart } from 'chart.js';

/**
 * Theme-aware colours for chart.js "ink" (canvas-drawn text + gridlines) that CSS can't reach.
 * Data-series colours (bar/line fills) are intentionally NOT touched here — those stay as each
 * chart defines them.
 */
export interface ChartColors {
  text: string;
  grid: string;
  gridStrong: string;
  gridFaint: string;
  axisTitle: string;
  neutralLine: string;
}

export const CHART_THEME: { light: ChartColors; dark: ChartColors } = {
  light: {
    text: '#666666',
    grid: 'rgba(0, 0, 0, 0.1)',
    gridStrong: 'rgba(15, 23, 42, 0.2)',
    gridFaint: 'rgba(15, 23, 42, 0.06)',
    axisTitle: '#64748b',
    neutralLine: '#0f172a'
  },
  dark: {
    text: 'rgba(255, 255, 255, 0.78)',
    grid: 'rgba(255, 255, 255, 0.12)',
    gridStrong: 'rgba(255, 255, 255, 0.28)',
    gridFaint: 'rgba(255, 255, 255, 0.08)',
    axisTitle: 'rgba(255, 255, 255, 0.7)',
    neutralLine: '#e4e2e2'
  }
};

export function chartColors(isDark: boolean): ChartColors {
  return isDark ? CHART_THEME.dark : CHART_THEME.light;
}

/**
 * Categorical data-viz palette — the colours chart SERIES are drawn with. Mirrors the `--c-data-*`
 * tokens in `src/styles/_semantic-colors.scss`; charts build their config in TS and can't read CSS vars,
 * so the values are duplicated here — KEEP THE TWO IN SYNC. These are semantic/categorical data
 * colours, NOT brand colours (a client's seed must not recolour a chart series).
 */
export const CHART_DATA = {
  // Generic single series (bar / line / scatter).
  primaryFill: '#42a5f5',
  primaryBorder: '#1976d2',
  primaryAreaFill: 'rgba(66, 165, 245, 0.2)', //   line area fill
  primaryPointFill: 'rgba(66, 165, 245, 0.5)', //  scatter points
  // Categorical series (pie) — distinct hues so slices stay legible.
  series: ['#42a5f5', '#66bb6a', '#ffa726', '#ef5350', '#ab47bc', '#bdbdbd'],
  // Cash-flow money series.
  income: '#3b82f6',
  incomeFill: 'rgba(59, 130, 246, 0.75)',
  expense: '#ef4444',
  expenseFill: 'rgba(239, 68, 68, 0.75)',
  // Weather domain colours (also surfaced in SCSS via --c-data-temp / --c-data-humidity).
  temp: '#ef6c00',
  tempFill: 'rgba(239, 108, 0, 0.2)',
  humidity: '#1565c0',
  humidityFill: 'rgba(21, 101, 192, 0.2)',
  // THI heat-stress bands (border/text colour + matching translucent fill).
  thi: { none: '#2e7d32', mild: '#f9a825', moderate: '#ef6c00', severe: '#c62828', extreme: '#7b1fa2' },
  thiFill: {
    none: 'rgba(67, 160, 71, 0.45)',
    mild: 'rgba(251, 192, 45, 0.5)',
    moderate: 'rgba(245, 124, 0, 0.5)',
    severe: 'rgba(229, 57, 53, 0.55)',
    extreme: 'rgba(142, 36, 170, 0.55)'
  }
} as const;

/**
 * Sets chart.js GLOBAL defaults for text + gridline colour. This is enough to theme any chart that
 * doesn't set its own tick/legend/title/grid colours (bar, line, pie, scatter). Charts with explicit
 * colours (cash-flow, weather) additionally re-derive those in their own theme effect. Idempotent.
 */
export function applyChartThemeDefaults(isDark: boolean): void {
  const c = chartColors(isDark);
  Chart.defaults.color = c.text;
  Chart.defaults.borderColor = c.grid;
}

/**
 * Re-themes a LIVE chart instance by writing explicit ink colours onto its options (legend labels,
 * axis ticks, gridlines, axis titles), then the caller calls `chart.update()`.
 *
 * Why this and not just `Chart.defaults` + update(): chart.js merges `Chart.defaults` into a chart's
 * config ONCE at creation, so changing the defaults later and calling update() does NOT re-theme an
 * already-created chart. Writing explicit values onto `chart.options` does take effect on update().
 *
 * Charts whose grid/ticks need bespoke logic (e.g. the cash-flow zero-line) should re-derive their
 * own colours instead of using this generic helper.
 */
export function applyChartThemeToChart(chart: Chart | undefined, isDark: boolean): void {
  if (!chart?.options) {
    return;
  }
  const c = chartColors(isDark);
  // chart.js' option types are deeply partial; cast to mutate nested colour fields cleanly.
  const opts = chart.options as unknown as {
    plugins?: { legend?: { labels?: Record<string, unknown> }; title?: Record<string, unknown> };
    scales?: Record<
      string,
      { ticks?: Record<string, unknown>; grid?: Record<string, unknown>; title?: Record<string, unknown>; border?: Record<string, unknown> }
    >;
  };

  opts.plugins ??= {};
  opts.plugins.legend ??= {};
  opts.plugins.legend.labels = { ...(opts.plugins.legend.labels ?? {}), color: c.text };
  if (opts.plugins.title) {
    opts.plugins.title['color'] = c.text;
  }

  if (opts.scales) {
    for (const key of Object.keys(opts.scales)) {
      const scale = opts.scales[key];
      if (!scale) {
        continue;
      }
      scale.ticks = { ...(scale.ticks ?? {}), color: c.text };
      scale.grid = { ...(scale.grid ?? {}), color: c.grid };
      if (scale.title) {
        scale.title['color'] = c.axisTitle;
      }
      if (scale.border) {
        scale.border['color'] = c.grid;
      }
    }
  }
}
